import { NOT_LOGGED_IN_USER_ID } from '@ori/auth';
import { fetchGraphql, getHeaders } from '@ori/fetching';
import type { FC, PropsWithChildren } from 'react';
import React, { createContext, useCallback, useMemo } from 'react';

import type { ContextValues } from './types';
import type {
  BackInStockNotificationSubscriptionInput,
  SubscribeToBackInStockMutation,
  SubscribeToBackInStockMutationVariables,
} from '../models';
import { subscribeToBackInStockNotification } from '../queries';

/* istanbul ignore next */
const defaultSubscribe = async () => ({});

export const AppContext = createContext<ContextValues>({
  notifyMeSubscriptionConfiguration: {
    IsCumulusMarket: false,
    IsEditionEnabled: false,
    IsSmsSubscriptionEnabled: false,
    MarketingApprovalWarningText: '',
    ShowMarketingApprovalWarning: false,
  },
  currentLanguage: '',
  customerId: NOT_LOGGED_IN_USER_ID,
  graphqlUrl: '',
  onlineBaseUrl: '',
  staticApiUrl: '',
  tenant: '',
  token: '',
  subscribe: defaultSubscribe,
});

export const AppContextProvider: FC<PropsWithChildren<Omit<ContextValues, 'subscribe'>>> = ({
  currentLanguage,
  graphqlUrl,
  notifyMeSubscriptionConfiguration,
  tenant,
  token,
  appName,
  children,
  customerId,
  onlineBaseUrl,
  staticApiUrl,
}) => {
  const subscribe = useCallback(
    async (input: BackInStockNotificationSubscriptionInput) =>
      fetchGraphql<SubscribeToBackInStockMutation, SubscribeToBackInStockMutationVariables>({
        query: subscribeToBackInStockNotification,
        url: graphqlUrl,
        headers: getHeaders({
          customerId,
          language: currentLanguage,
          sourceAppName: appName,
          tenant,
          token,
        }),
        variables: {
          input,
        },
      }),
    [appName, currentLanguage, customerId, graphqlUrl, tenant, token],
  );

  return (
    <AppContext.Provider
      value={useMemo(
        () => ({
          currentLanguage,
          graphqlUrl,
          notifyMeSubscriptionConfiguration,
          tenant,
          token,
          appName,
          children,
          customerId,
          onlineBaseUrl,
          staticApiUrl,
          subscribe,
        }),
        [
          appName,
          children,
          currentLanguage,
          customerId,
          graphqlUrl,
          notifyMeSubscriptionConfiguration,
          onlineBaseUrl,
          staticApiUrl,
          subscribe,
          tenant,
          token,
        ],
      )}
    >
      {children}
    </AppContext.Provider>
  );
};
